import { useState, useEffect } from 'react';
import { customersApi, Customer, CreateCustomerData } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';

function CustomersManager() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadCustomers();
  }, [search]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await customersApi.getAll(undefined, search || undefined);
      setCustomers(data.customers);
      setTotal(data.total);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Ошибка при загрузке покупателей');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data: CreateCustomerData) => {
    try {
      await customersApi.create(data);
      setShowCreateForm(false);
      await loadCustomers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Ошибка при создании покупателя');
    }
  };

  const handleUpdate = async (id: string, data: Partial<Customer>) => {
    try {
      await customersApi.update(id, {
        name: data.name || undefined,
        phone: data.phone || undefined,
        email: data.email || undefined,
        discountCardNumber: data.discountCardNumber || undefined,
        msId: data.msId || undefined,
      });
      setEditingCustomer(null);
      await loadCustomers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Ошибка при обновлении покупателя');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Вы уверены, что хотите удалить этого покупателя?')) {
      return;
    }
    try {
      await customersApi.delete(id);
      await loadCustomers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Ошибка при удалении покупателя');
    }
  };

  return (
    <div className="bg-card rounded-lg p-6 w-full">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-foreground m-0">Управление покупателями</h1>
        <Button onClick={() => setShowCreateForm(true)}>
          Добавить покупателя
        </Button>
      </div>

      <div className="mb-4">
        <Input
          type="text"
          placeholder="Поиск по имени, телефону, email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full"
        />
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {showCreateForm && (
        <CustomerForm
          onSave={handleCreate}
          onCancel={() => setShowCreateForm(false)}
        />
      )}

      {editingCustomer && (
        <CustomerForm
          customer={editingCustomer}
          onSave={(data) => handleUpdate(editingCustomer.id, data)}
          onCancel={() => setEditingCustomer(null)}
        />
      )}

      {loading ? (
        <div className="text-center py-10 text-muted-foreground">Загрузка...</div>
      ) : (
        <div>
          {customers.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              {search ? 'Покупатели не найдены' : 'Покупатели не добавлены'}
            </div>
          ) : (
            <>
              <div className="mb-4 text-sm text-muted-foreground">Найдено: {total}</div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Имя</TableHead>
                    <TableHead>Телефон</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Номер карты</TableHead>
                    <TableHead>ID МойСклад</TableHead>
                    <TableHead>Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customers.map((customer) => (
                    <TableRow key={customer.id}>
                      <TableCell>{customer.name || '-'}</TableCell>
                      <TableCell>{customer.phone || '-'}</TableCell>
                      <TableCell>{customer.email || '-'}</TableCell>
                      <TableCell>{customer.discountCardNumber || '-'}</TableCell>
                      <TableCell>{customer.msId || '-'}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingCustomer(customer)}
                          >
                            Изменить
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDelete(customer.id)}
                          >
                            Удалить
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface CustomerFormProps {
  customer?: Customer;
  onSave: (data: CreateCustomerData) => void;
  onCancel: () => void;
}

function CustomerForm({ customer, onSave, onCancel }: CustomerFormProps) {
  const [name, setName] = useState(customer?.name || '');
  const [phone, setPhone] = useState(customer?.phone || '');
  const [email, setEmail] = useState(customer?.email || '');
  const [discountCardNumber, setDiscountCardNumber] = useState(
    customer?.discountCardNumber || ''
  );
  const [msId, setMsId] = useState(customer?.msId || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name && !phone && !email) {
      alert('Укажите хотя бы одно из полей: имя, телефон или email');
      return;
    }
    onSave({
      name: name || undefined,
      phone: phone || undefined,
      email: email || undefined,
      discountCardNumber: discountCardNumber || undefined,
      msId: msId || undefined,
    });
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{customer ? 'Редактировать покупателя' : 'Добавить покупателя'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Имя</Label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Телефон</Label>
            <Input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Номер дисконтной карты</Label>
            <Input
              type="text"
              value={discountCardNumber}
              onChange={(e) => setDiscountCardNumber(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>ID МойСклад</Label>
            <Input
              type="text"
              value={msId}
              onChange={(e) => setMsId(e.target.value)}
            />
          </div>
          <div className="flex gap-3 justify-end mt-6">
            <Button type="submit">
              {customer ? 'Сохранить' : 'Создать'}
            </Button>
            <Button type="button" variant="destructive" onClick={onCancel}>
              Отмена
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default CustomersManager;

